// The single-page app's entry (plan §3.1), loaded by Views/Spa/Shell.cshtml: the site's only script.

import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { QueryClient } from "@tanstack/react-query";
import { loadPage, setNavigator } from "@core/navigation";
import { AppProviders } from "./app/AppProviders";
import { installLinkHandler } from "./app/links";
import { installPrefetch, prefetchRoute } from "./app/prefetch";
import { isAppPath, loadLazyRoutes, routesFor } from "./app/routes";
import { applySession, readBootstrappedSession, SIGNED_OUT_SESSION } from "./app/session";

const root = document.getElementById("app")!;

const queryClient = new QueryClient();
const session = readBootstrappedSession() ?? SIGNED_OUT_SESSION;
applySession(queryClient, session);

// The admin pages only for an admin: for anyone else their addresses are the server's (its 404 page)
const routes = routesFor(session);
const isAppPage = (pathname: string) => isAppPath(pathname, routes);

// A prerendered page is hydrated, so its module must be loaded before the first render.
await loadLazyRoutes(window.location.pathname, routes);

const router = createBrowserRouter(routes);
installLinkHandler(isAppPage, (to, replace) => void router.navigate(to, { replace }));
// A page that fails to load ahead is loaded as before when its link is clicked
installPrefetch(isAppPage, to => void prefetchRoute(to, routes, queryClient).catch(() => {}));

// navigateTo() from code (a dialog's redirect, a page's): in place for the app's pages, a page load for anything else
setNavigator((url, options) => {
    const target = new URL(url, window.location.href);

    if (target.origin === window.location.origin && isAppPage(target.pathname))
        void router.navigate(target.pathname + target.search + target.hash, { replace: options?.replace });
    else
        loadPage(url, options);
});

const app = (
    <StrictMode>
        <AppProviders queryClient={queryClient}>
            <RouterProvider router={router} />
        </AppProviders>
    </StrictMode>
);

if (root.hasChildNodes())
    hydrateRoot(root, app);
else
    createRoot(root).render(app);
